/**
 * API Service - Connects to the new PostgreSQL backend
 * This service replaces Supabase calls with REST API calls
 * Only used in branches that use the new backend (not main branch)
 */

import { API_BASE_URL } from '../config/apiConfig';

class ApiService {
  public async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // Increased to 30s timeout

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include',
      signal: controller.signal,
      ...options,
    };

    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      };
    }

    try {
      let response: Response;
      try {
        response = await fetch(url, config);
      } catch (error: any) {
        // Automatically retry ONCE for AbortError (timeout)
        if (error.name === 'AbortError') {
          console.warn(`⚠️ Request to ${endpoint} timed out. Retrying once with 45s timeout...`);
          const retryController = new AbortController();
          const retryTimeoutId = setTimeout(() => retryController.abort(), 45000);
          try {
            response = await fetch(url, { ...config, signal: retryController.signal });
            clearTimeout(retryTimeoutId);
          } catch (retryError) {
            clearTimeout(retryTimeoutId);
            throw retryError;
          }
        } else {
          throw error;
        }
      }
      clearTimeout(timeoutId);

      if (!response.ok) {
        // Handle 404 Not Found gracefully for GET requests
        if (response.status === 404 && (!options.method || options.method === 'GET')) {
          // Silent 404 for guides as they are often expected due to stale data
          if (endpoint.includes('/guides/')) {
            // Log very concisely only for debugging
            console.debug(`ℹ️ Guide ${endpoint.split('/').pop()} not found (404)`);
            return null as any;
          }

          console.warn(`⚠️ Resource not found (404): ${url}`);
          // Return empty array for known collection endpoints
          if (endpoint.includes('/tours') || endpoint.includes('/categories')) {
            return [] as any;
          }
          return null as any;
        }

        // Handle rate limiting specifically
        if (response.status === 429) {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('rate-limit-exceeded'));
          }
          const errorText = await response.text().catch(() => 'Too many requests');
          console.warn('⚠️ Rate limit hit (429). Waiting 2 seconds before retry...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          // Retry once
          const retryResponse = await fetch(url, config);
          if (!retryResponse.ok) {
            const retryError = await retryResponse.json().catch(() => ({ error: retryResponse.statusText }));
            throw new Error(retryError.error || `HTTP error! status: ${retryResponse.status}`);
          }
          const retryData = await retryResponse.json();
          return (retryData.data !== undefined) ? retryData.data : retryData;
        }

        if (response.status === 401) {
          // Only propagate auth-expiry events for protected endpoints, not for the
          // session-check on the login page itself (which legitimately returns 401).
          if (endpoint !== '/auth/me') {
            console.warn('🚫 API returned 401 Unauthorized. Clearing stale token.');
            localStorage.removeItem('authToken');
            window.dispatchEvent(new Event('auth-expired'));
          }
        }

        const error = await response.json().catch(() => ({ error: response.statusText }));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Backend returns { success: true, data: {...} } format
      const result = (data.data !== undefined) ? data.data : data;
      return result;
    } catch (error: any) {
      clearTimeout(timeoutId);
      // Suppress noisy console.error for expected unauthenticated session checks
      const isExpectedAuthCheck = endpoint === '/auth/me' && error?.message?.includes('No token provided');
      if (!isExpectedAuthCheck) {
        console.error(`❌ API request failed: ${endpoint}`, error);
        if (error?.message?.includes('fetch')) {
          console.error(`   Network error - check if backend is running on ${API_BASE_URL}`);
        }
      }
      throw error;
    }
  }

  // Tours
  async getAllTours(activeOnly: boolean = false): Promise<any[]> {
    const query = activeOnly ? '?activeOnly=true' : '';
    return this.request<any[]>(`/tours${query}`);
  }

  async getTourById(id: string): Promise<any | null> {
    return this.request<any>(`/tours/${id}`);
  }

  async getToursByCategoryId(categoryId: string): Promise<any[]> {
    const encodedCategoryId = encodeURIComponent(categoryId);
    const url = `/tours?categoryId=${encodedCategoryId}`;

    try {
      const data = await this.request<any>(url);
      
      // Backend returns: { success: true, count: number, data: Tour[] }
      // The request method already handles extracting .data if it exists
      if (Array.isArray(data)) {
        return data;
      }
      
      console.warn(`⚠️ No tours found for categoryId: ${categoryId}`);
      return [];
    } catch (error) {
      console.error('❌ ApiService.getToursByCategoryId - Error:', error);
      return [];
    }
  }

  async createTour(tourData: any): Promise<any> {
    return this.request<any>('/tours', {
      method: 'POST',
      body: JSON.stringify(tourData),
    });
  }

  async updateTour(id: string, tourData: any): Promise<any> {
    return this.request<any>(`/tours/${id}`, {
      method: 'PUT',
      body: JSON.stringify(tourData),
    });
  }

  async deleteTour(id: string): Promise<boolean> {
    await this.request(`/tours/${id}`, {
      method: 'DELETE',
    });
    return true;
  }

  async getTourStats(): Promise<{
    totalTours: number;
    activeTours: number;
    totalRevenue: number;
    avgRating: number;
  }> {
    return this.request<{
      totalTours: number;
      activeTours: number;
      totalRevenue: number;
      avgRating: number;
    }>('/tours/stats');
  }

  // Reviews
  async getTourReviews(tourId: string): Promise<any[]> {
    return this.request<any[]>(`/tours/${tourId}/reviews`);
  }

  async addReview(tourId: string, reviewData: {
    userName: string;
    rating: number;
    title: string;
    comment: string;
  }): Promise<any> {
    return this.request<any>(`/tours/${tourId}/reviews`, {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  }

  // Categories
  async getAllCategories(activeOnly: boolean = false): Promise<any[]> {
    const query = activeOnly ? '?activeOnly=true' : '';
    return this.request<any[]>(`/categories${query}`);
  }

  async getCategoryById(id: string): Promise<any | null> {
    return this.request<any>(`/categories/${id}`);
  }

  async createCategory(categoryData: any): Promise<any> {
    return this.request<any>('/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
    });
  }

  async updateCategory(id: string, categoryData: any): Promise<any> {
    return this.request<any>(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
    });
  }

  async deleteCategory(id: string): Promise<boolean> {
    await this.request(`/categories/${id}`, {
      method: 'DELETE',
    });
    return true;
  }

  // Images
  async getAllImages(activeOnly: boolean = false): Promise<any[]> {
    const query = activeOnly ? '?activeOnly=true' : '';
    return this.request<any[]>(`/images${query}`);
  }

  async getImagesByCategory(category: string): Promise<any[]> {
    return this.request<any[]>(`/images?category=${category}`);
  }

  async getImageById(id: string): Promise<any | null> {
    return this.request<any>(`/images/${id}`);
  }

  async createImage(imageData: any): Promise<any> {
    return this.request<any>('/images', {
      method: 'POST',
      body: JSON.stringify(imageData),
    });
  }

  // File upload (Cloudinary)
  async uploadToCloudinary(file: File): Promise<{ url: string; success: boolean; message: string }> {
    const token = localStorage.getItem('authToken');
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Upload failed with status: ${response.status}`);
    }

    return await response.json();
  }

  async updateImage(id: string, imageData: any): Promise<any> {
    return this.request<any>(`/images/${id}`, {
      method: 'PUT',
      body: JSON.stringify(imageData),
    });
  }

  async deleteImage(id: string): Promise<boolean> {
    await this.request(`/images/${id}`, {
      method: 'DELETE',
    });
    return true;
  }

  // Authentication
  async login(email: string, password: string): Promise<{ user: any; token: string }> {
    const response = await this.request<{ user: any; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    // Backend now sets an HttpOnly cookie — no localStorage needed
    // Keep token in response for any components that need it as a reference
    return response;
  }

  async register(email: string, password: string, name: string, phone?: string): Promise<{ user: any; token: string }> {
    const response = await this.request<{ user: any; token: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, phone }),
    });

    // Backend now sets an HttpOnly cookie — no localStorage needed
    return response;
  }

  async getCurrentUser(): Promise<any | null> {
    // If there is no token at all, skip the network round-trip and return null
    // immediately. This prevents the noisy 401 spam on the login page.
    const hasToken =
      document.cookie.split(';').some(c => c.trim().startsWith('st_session=')) ||
      !!localStorage.getItem('authToken');
    if (!hasToken) return null;
    return this.request<any>('/auth/me');
  }

  // Guides
  async getAllGuides(activeOnly: boolean = false): Promise<any[]> {
    const query = activeOnly ? '?activeOnly=true' : '';
    return this.request<any[]>(`/guides${query}`);
  }

  async getGuideById(id: string): Promise<any | null> {
    return this.request<any>(`/guides/${id}`);
  }

  async createGuide(guideData: any): Promise<any> {
    return this.request<any>('/guides', {
      method: 'POST',
      body: JSON.stringify(guideData),
    });
  }

  async updateGuide(id: string, guideData: any): Promise<any> {
    return this.request<any>(`/guides/${id}`, {
      method: 'PUT',
      body: JSON.stringify(guideData),
    });
  }

  async deleteGuide(id: string): Promise<boolean> {
    await this.request(`/guides/${id}`, {
      method: 'DELETE',
    });
    return true;
  }

  // Pricing
  async calculatePrice(tour: any, context: any): Promise<any> {
    return this.request<any>('/pricing/calculate', {
      method: 'POST',
      body: JSON.stringify({ tour, context }),
    });
  }

  // Currency
  async getExchangeRates(): Promise<{ [key: string]: number }> {
    return this.request<{ [key: string]: number }>('/currency/rates');
  }

  // Bookings
  async createBooking(bookingData: any): Promise<any> {
    return this.request<any>('/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  }

  async updateBooking(id: string, bookingData: any): Promise<any> {
    return this.request<any>(`/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(bookingData),
    });
  }

  async getAllBookings(): Promise<any[]> {
    return this.request<any[]>('/bookings');
  }

  async getBookingById(id: string): Promise<any> {
    return this.request<any>(`/bookings/${id}`);
  }

  async updateBookingStatus(id: string, status: string, paymentStatus?: string): Promise<any> {
    return this.request<any>(`/bookings/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status, paymentStatus }),
    });
  }

  // Payments (New)
  async recordPayment(paymentData: any): Promise<any> {
    return this.request<any>('/payments/record', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async verifyPayment(paymentId: string): Promise<any> {
    return this.request<any>(`/payments/${paymentId}/verify`, {
      method: 'POST'
    });
  }

  async getPaymentHistory(bookingId: string): Promise<any[]> {
    return this.request<any[]>(`/payments/booking/${bookingId}`);
  }

  async downloadReceipt(bookingId: string): Promise<void> {
    const token = localStorage.getItem('authToken');
    const url = `${API_BASE_URL}/receipts/${bookingId}`;
    
    const response = await fetch(url, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to download receipt: ${response.statusText}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = `Receipt_${bookingId}.pdf`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) fileName = match[1];
    }
    
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  }

  // Logistics (New)
  async getLogisticsSummary(): Promise<any[]> {
    return this.request<any[]>('/logistics/summary');
  }

  async getLogisticsAggregation(): Promise<any[]> {
    return this.request<any[]>('/logistics');
  }

  async getAllGroups(): Promise<any[]> {
    return this.request<any[]>('/logistics'); // For now, logistics endpoint returns the list of groups
  }

  logout(): void {
    // Clear the local storage token (legacy support)
    localStorage.removeItem('authToken');
    // Call the backend to clear the HttpOnly session cookie
    this.request('/auth/logout', { method: 'POST' }).catch(() => { });
  }

  // Admin Settings
  async getSettings(): Promise<{ admin_whatsapp_phones: string[]; gift_category_order: string[] }> {
    return this.request<{ admin_whatsapp_phones: string[]; gift_category_order: string[] }>('/settings');
  }

  async updateSettings(data: { admin_whatsapp_phones?: string[]; gift_category_order?: string[] }): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/settings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async googleLogin(credential: string, isAdminPortal: boolean = true): Promise<any> {
    return this.request<any>('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ credential, isAdminPortal }),
    });
  }

  // Admin management
  async getAdmins(): Promise<any[]> {
    return this.request<any[]>('/admins');
  }

  async updateAdminStatus(id: string, status: 'approved' | 'rejected'): Promise<any> {
    return this.request<any>(`/admins/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async updateAdminPermissions(id: string, permissions: string[]): Promise<any> {
    return this.request<any>(`/admins/${id}/permissions`, {
      method: 'PUT',
      body: JSON.stringify({ permissions }),
    });
  }

  public async get<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  public async post<T>(endpoint: string, body: any, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  public async put<T>(endpoint: string, body: any, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  public async delete<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const apiService = new ApiService();
export default apiService;

